import { Popover } from "frost-ui-react";
import { useRef, useState } from "react";
import styled from "styled-components";
import useOutsideAlerter from "../../../hooks/useOutsideAlerter";
import Flex from "../../Layout/Flex";
import AppIcon from "../AppIcon/AppIcon";
import AppText from "../AppText/AppText";

interface ISelectPopoverProps<O> {
	label: string;
	onSelect: (option: O, i: number) => void;
	options: O[];
	className?: string;
	value?: string;
	disabled?: boolean;
	placeholder?: string;
	popoverWidth?: number;

	/**
	 * If popover should be aligned left (default) or right
	 */
	popoverPosition?: "left" | "right";
	getOptionFromValue?: (value: string | undefined, options: O[]) => O | undefined;
	getLabelFromOption?: (o?: O) => string;
	getValueFromOption?: (o?: O) => string;
	hideLabelAsPrefix?: boolean;
}

export function SelectPopover<O = string>({
	label,
	options,
	onSelect,
	className,
	value,
	disabled,
	popoverWidth = 350,
	popoverPosition = "left",
	placeholder = label,
	getOptionFromValue,
	getLabelFromOption,
	getValueFromOption,
	hideLabelAsPrefix,
}: ISelectPopoverProps<O>) {
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	useOutsideAlerter(wrapperRef, close, "mousedown", [close]);

	function close() {
		setOpen(false);
	}

	function handleSelect(option: O, i: number) {
		onSelect(option, i);
		close();
	}

	function keyExtractor(o: O) {
		return JSON.stringify(o);
	}

	const valueToOptionMapperFn =
		getOptionFromValue || ((value) => options.find((o) => (o as unknown) === value));
	const optionToLabelMapperFn = getLabelFromOption || ((option) => option as unknown);
	const optionToValueMapperFn = getValueFromOption || ((option) => option as unknown);

	const selectedOption = valueToOptionMapperFn(value, options);
	const selectedOptionLabel = optionToLabelMapperFn(selectedOption);

	function renderLabelOrPlaceholder() {
		if (!value) {
			return <AppText color="grayTextLight">{placeholder}</AppText>;
		} else if (!hideLabelAsPrefix) {
			return <AppText color="grayTextLight">{label}:</AppText>;
		}
		return null;
	}

	return (
		<StyledSelectPopover
			ref={wrapperRef}
			className={className}
			style={{ pointerEvents: disabled ? "none" : "auto", opacity: disabled ? 0.6 : 1 }}
		>
			<div className="label-ctn" onClick={() => setOpen(!open)}>
				<Flex gap="0.4rem">
					{renderLabelOrPlaceholder()}

					<AppText margin="0 0.3rem 0 0" textDecoration="underline">
						{selectedOptionLabel + ""}
					</AppText>
				</Flex>
				<AppIcon icon="dropdown" size="12px" />
			</div>

			{open && (
				<div className={`popover-ctn popover-ctn-${popoverPosition}`}>
					<Popover
						pointTo="top"
						styles={{
							padding: "15px 8px",
							width: popoverWidth,
							offset: popoverPosition === "left" ? undefined : String(popoverWidth - 50) + "px",
						}}
					>
						{options.map((o, i) => (
							<div key={keyExtractor(o)} className="row" onClick={() => handleSelect(o, i)}>
								<div
									className="icon-ctn"
									style={{ opacity: optionToValueMapperFn(o) === value ? 1 : 0 }}
								>
									<AppIcon icon="dark-check" size="10px" />
								</div>
								<AppText margin="0 0 0 0.25rem">{optionToLabelMapperFn(o) + ""}</AppText>
							</div>
						))}
					</Popover>
				</div>
			)}
		</StyledSelectPopover>
	);
}

export default SelectPopover;

const StyledSelectPopover = styled.div`
	.label-ctn {
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: space-between;
		user-select: none;
	}

	position: relative;

	.row {
		cursor: pointer;
		margin: 0.75rem 0;
		display: flex;
		align-items: center;

		&:first-child {
			margin-top: 0;
		}

		&:last-child {
			margin-bottom: 0;
		}

		&:hover {
			opacity: 0.5;
		}
	}

	.popover-ctn {
		position: absolute;
		top: 2rem;
		/* Above sticky tab bars (Tailwind z-20), below layout overlays (z-index: 100). */
		z-index: 30;
	}

	.popover-ctn-left {
		left: 0;
	}

	.popover-ctn-right {
		right: 0;
	}
`;
